import { COUNTRY_AGENCIES, COPYRIGHT_OFFICE_COUNTRIES, NO_CMO_COUNTRIES } from "../data/countryAgencies";
import { useAuth } from "../context/AuthContext";

// Blur rule (universal for OSRR):
//   - Guest / non-registered visitors see agency names BLURRED across every directory & card.
//   - Any signed-in registered user sees crisp, fully-legible names.
// Tease mode (`onTease` callback) also gates clicks so they open the auth modal
// instead of opening the external agency site.
export default function CountryFlagGrid({ onTease }) {
  const { user } = useAuth();
  const teaseMode = typeof onTease === "function";
  // Blur names for guests / non-registered viewers ONLY. Signed-in users see clear names.
  const blurNames = !user;
  const totalCountries = COUNTRY_AGENCIES.length + COPYRIGHT_OFFICE_COUNTRIES.length + NO_CMO_COUNTRIES.length;
  return (
    <section className="section-pad" data-testid="country-flag-grid-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-8 mb-10">
          <div className="lg:col-span-7">
            <div className="overline">Global reach · {totalCountries} territories</div>
            <h2 className="font-display text-3xl lg:text-5xl tracking-tight mt-3 leading-[1.05]">
              Tap any flag — meet the agency that protects{" "}
              <span className="text-[#7DA3D9]">composers in that country</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-slate-300 leading-relaxed">
              The world is split three ways: countries with a fully-operating music royalty
              society, countries that only have a copyright office, and countries that have no
              functioning music rights body at all. We show all three so you know exactly where
              you stand.
            </p>
          </div>
        </div>

        {/* ===== Tier 1: Full national/regional CMO ===== */}
        <Tier
          label="Active national or regional music rights society"
          count={COUNTRY_AGENCIES.length}
          countries={COUNTRY_AGENCIES}
          accent="#D4AF37"
          tone="active"
          teaseMode={teaseMode}
          blurNames={blurNames}
          onTease={onTease}
          testId="flag-tier-active"
        />

        {/* ===== Tier 2: Copyright office only ===== */}
        <div className="mt-16 pt-10 border-t border-white/10" data-testid="flag-tier-office">
          <div className="max-w-3xl mb-6">
            <div className="overline" style={{ color: "#7DA3D9" }}>Copyright office only · partial coverage</div>
            <h3 className="font-display text-2xl lg:text-3xl mt-2 tracking-tight">
              {COPYRIGHT_OFFICE_COUNTRIES.length} countries with a copyright office or foreign reciprocal — but no full music CMO
            </h3>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              These countries have a copyright registry or rely on a foreign society (SACEM, SIAE)
              under reciprocal agreement, but they do not yet run a fully-functioning national
              music royalty collection society of their own. Royalties can still be claimed —
              but via the listed office or reciprocal partner.
            </p>
          </div>
          <Tier
            label=""
            count={COPYRIGHT_OFFICE_COUNTRIES.length}
            countries={COPYRIGHT_OFFICE_COUNTRIES}
            accent="#7DA3D9"
            tone="office"
            teaseMode={teaseMode}
            blurNames={blurNames}
            onTease={onTease}
            testId="flag-tier-office-grid"
            heading={false}
          />
        </div>

        {/* ===== Tier 3: No CMO ===== */}
        <div className="mt-16 pt-10 border-t border-white/10" data-testid="flag-tier-none">
          <div className="max-w-3xl mb-6">
            <div className="overline" style={{ color: "#7DA3D9" }}>No functioning music rights body</div>
            <h3 className="font-display text-2xl lg:text-3xl mt-2 tracking-tight">
              {NO_CMO_COUNTRIES.length} countries have <span className="text-slate-400">no</span> functioning music rights body
            </h3>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              These countries currently have <span className="text-white font-medium">no operational national
              music CMO and no foreign reciprocal partner</span>. Royalties generated inside these
              territories are largely uncollected. Each tile links to the country&apos;s IP office
              or government portal where one exists — the gap on the map is intentional and real.
            </p>
          </div>
          <Tier
            label=""
            count={NO_CMO_COUNTRIES.length}
            countries={NO_CMO_COUNTRIES}
            accent="#94A3B8"
            tone="none"
            teaseMode={teaseMode}
            blurNames={blurNames}
            onTease={onTease}
            testId="flag-tier-none-grid"
            heading={false}
          />
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-400">
          <span>{totalCountries} territories total · OSRR international registry</span>
          <span className="overline">OSRR global registry</span>
        </div>
      </div>
    </section>
  );
}

function Tier({ countries, tone, teaseMode, blurNames, onTease, testId }) {
  return (
    <div
      className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3"
      data-testid={testId}
    >
      {countries.map((c) => {
        const baseClass =
          "group flex flex-col rounded-md overflow-hidden border bg-white/[0.02] hover:bg-white/[0.05] hover:-translate-y-0.5 transition-all text-left";
        const borderClass =
          tone === "active"
            ? "border-white/10 hover:border-[#7DA3D9]"
            : tone === "office"
            ? "border-[#7DA3D9]/20 hover:border-[#7DA3D9]"
            : "border-slate-600/30 hover:border-slate-500 opacity-90";
        const fullClass = `${baseClass} ${borderClass}`;
        const inner = (
          <>
            <div className="aspect-[4/3] w-full overflow-hidden bg-black/30">
              <img
                src={`https://flagcdn.com/w160/${c.cc.toLowerCase()}.png`}
                alt={`${c.country} flag`}
                loading="lazy"
                className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                  tone === "none" ? "grayscale-[40%] group-hover:grayscale-0" : ""
                }`}
              />
            </div>
            <div className="px-2 py-2 text-center">
              <div className="text-[11px] text-slate-400 truncate">{c.country}</div>
              <div
                className={`text-xs font-semibold truncate group-hover:text-[#7DA3D9] transition-colors ${
                  tone === "none" ? "text-slate-300" : "text-white"
                } ${blurNames ? "blur-sm select-none" : ""}`}
              >
                {c.agency}
              </div>
            </div>
          </>
        );
        return teaseMode ? (
          <button
            key={c.cc}
            type="button"
            onClick={() => onTease(`flag-${c.cc}`)}
            className={fullClass + " w-full"}
            title={`${c.country} — ${c.agency}`}
            data-testid={`flag-tease-${c.cc}`}
          >
            {inner}
          </button>
        ) : (
          <a
            key={c.cc}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            className={fullClass}
            title={`${c.country} — ${c.agency}`}
            data-testid={`flag-link-${c.cc}`}
          >
            {inner}
          </a>
        );
      })}
    </div>
  );
}
